import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ArrowType } from '@/components/game/player/ArrowType';

/**
 * Interface for player state
 */
interface PlayerState {
  health: number;
  maxHealth: number;
  position: { x: number; y: number; z: number };
  rotation: { x: number; y: number; z: number };
  score: number;
  highScore: number;
  arrowInventory: Record<ArrowType, number>;
  currentArrowType: ArrowType;
  isAiming: boolean;
  drawStrength: number;
  kills: number;
  level: number;
  experience: number;
  experienceToNextLevel: number;
  resources: {
    gold: number;
  };
}

/**
 * Initial player state
 */
const initialState: PlayerState = {
  health: 100,
  maxHealth: 100,
  position: { x: 0, y: 5, z: 0 },
  rotation: { x: 0, y: 0, z: 0 },
  score: 0,
  highScore: 0,
  arrowInventory: {
    [ArrowType.NORMAL]: -1,
    [ArrowType.FIRE]: 10,
    [ArrowType.EXPLOSIVE]: 5,
    [ArrowType.POISON]: 10
  },
  currentArrowType: ArrowType.NORMAL,
  isAiming: false,
  drawStrength: 0,
  kills: 0,
  level: 1,
  experience: 0,
  experienceToNextLevel: 100,
  resources: {
    gold: 500
  }
};

/**
 * Player slice for Redux
 */
const playerSlice = createSlice({
  name: 'player',
  initialState,
  reducers: {
    /**
     * Updates the player's health
     */
    updatePlayerHealth: (state, action: PayloadAction<number>) => {
      state.health = Math.max(0, Math.min(action.payload, state.maxHealth));
    },
    
    /**
     * Updates the player's maximum health
     */
    updatePlayerMaxHealth: (state, action: PayloadAction<number>) => {
      state.maxHealth = action.payload;
      state.health = Math.min(state.health, state.maxHealth);
    },
    
    /**
     * Updates the player's score
     */
    updatePlayerScore: (state, action: PayloadAction<number>) => {
      state.score = action.payload;
      
      // Update high score if needed
      if (state.score > state.highScore) {
        state.highScore = state.score;
      }
    },
    
    /**
     * Updates the player's arrow inventory
     */
    updateArrowInventory: (state, action: PayloadAction<{ type: ArrowType, count: number }>) => {
      const { type, count } = action.payload;
      state.arrowInventory[type] = Math.max(0, count);
    },
    
    /**
     * Sets the current arrow type
     */
    setCurrentArrowType: (state, action: PayloadAction<ArrowType>) => {
      state.currentArrowType = action.payload;
    },
    
    /**
     * Uses an arrow of the specified type
     */
    useArrow: (state, action: PayloadAction<ArrowType>) => {
      const type = action.payload;
      if (state.arrowInventory[type] > 0) {
        state.arrowInventory[type]--;
      }
    },
    
    /**
     * Adds arrows of the specified type
     */
    addArrows: (state, action: PayloadAction<{ type: ArrowType, count: number }>) => {
      const { type, count } = action.payload;
      state.arrowInventory[type] += count;
    },
    
    /**
     * Resets the player state
     */
    resetPlayerState: (state) => {
      state.health = initialState.health;
      state.score = 0;
      state.arrowInventory = { ...initialState.arrowInventory };
      state.currentArrowType = initialState.currentArrowType;
    },

    setPlayerPosition: (state, action: PayloadAction<{ x: number; y: number; z: number }>) => {
      state.position = action.payload;
    },
    setPlayerRotation: (state, action: PayloadAction<{ x: number; y: number; z: number }>) => {
      state.rotation = action.payload;
    },
    setAiming: (state, action: PayloadAction<boolean>) => {
      state.isAiming = action.payload;
    },
    setDrawStrength: (state, action: PayloadAction<number>) => {
      state.drawStrength = Math.min(Math.max(action.payload, 0), 1);
    },
    addKill: (state) => {
      state.kills += 1;
    },
    addExperience: (state, action: PayloadAction<number>) => {
      state.experience += action.payload;
      while (state.experience >= state.experienceToNextLevel) {
        state.experience -= state.experienceToNextLevel;
        state.level += 1;
        state.experienceToNextLevel = Math.floor(state.experienceToNextLevel * 1.5);
        state.maxHealth += 10;
        state.health = state.maxHealth;
      }
    },
    /**
     * Updates player resources
     */
    updatePlayerResources: (state, action: PayloadAction<{ gold: number }>) => {
      state.resources.gold = action.payload.gold;
    },
  }
});

export const {
  updatePlayerHealth,
  updatePlayerMaxHealth,
  updatePlayerScore,
  updateArrowInventory,
  setCurrentArrowType,
  useArrow,
  addArrows,
  resetPlayerState,
  setPlayerPosition,
  setPlayerRotation,
  setAiming,
  setDrawStrength,
  addKill,
  addExperience,
  updatePlayerResources,
} = playerSlice.actions;

export default playerSlice.reducer; 